import { Row, Col, Flex, Tag, Button } from "antd";
import React, { useEffect, useState } from "react";
import { IApplicant, IJob, ProjectsMainWrapper, recruiterVisibleStatuses } from "./helper";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeading from "./pageHeading";
import { axiosInstant, endpoints } from "../../api";
import { showError } from "../../Notifications";
import BackBtn from "../backBtn";
import ProjectList from "./projectsPanel/projectList";
import ApplicantsListPanel from "./applicantsPanel/applicantsListPanel";
import RecruiterActionDashboard from "./RecruiterActionDashboard";

const ACTION_STATUS_LABELS: Record<string, string> = {
  submitted: "Needs Screening",
  rejected_manager: "Send Rejection",
  approved_manager: "Schedule 2nd Interview",
};

export const RecruitersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobsList, setJobsList] = useState<Array<IJob>>([]);
  const [selectedJob, setSelectedJob] = useState<IJob | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  // Opens the manager-mode scheduling calendar (candidate + hiring managers)
  // for the 2nd interview. This was previously an empty stub - the backend and
  // the calendar screen it needs (isManagerMode in AvailabilityCalendar) have
  // always supported this; nothing in the app ever linked to it.
  const scheduleCall = (applicant: IApplicant, isReschedule = false) => {
    if (!applicant.applicantId || !selectedJob?._id || !applicant.jobApplicationId) {
      showError("Missing candidate, job, or application ID");
      return;
    }
    navigate(
      `/recruiter/interview-calendar?applicantId=${applicant.applicantId}&jobId=${selectedJob._id}&jobApplicationId=${applicant.jobApplicationId}${isReschedule ? '&reschedule=true' : ''}`
    );
  };

  useEffect(() => {
    axiosInstant
      .get(endpoints.jobs.recruiter.myJobs)
      .then(async (e) => {
        const jobs = e.data;
        const jobsWithCounts = await Promise.all(
          jobs.map(async (job: IJob) => {
            try {
              const res = await axiosInstant.get(`/job-applications/applications/by-job/${job._id}`);
              const applicants = Array.isArray(res.data) ? res.data : [];
              const visibleApplicants = applicants.filter((app: any) => recruiterVisibleStatuses.includes(app.status ?? ""));
              const actionCounts: Record<string, number> = {};
              visibleApplicants.forEach((app: any) => {
                if (app.status) actionCounts[app.status] = (actionCounts[app.status] || 0) + 1;
              });
              return { ...job, totalApplicants: visibleApplicants.length, actionCounts };
            } catch {
              return { ...job, totalApplicants: 0, actionCounts: {} };
            }
          })
        );
        setJobsList(jobsWithCounts);
      })
      .catch((err) => showError(err));
  }, [searchParams]);

  useEffect(() => {
    if (!jobsList.length) return;
    const jobParam = searchParams.get("job") || -1;
    const job = jobsList.find((e) => e._id === jobParam);
    if (job) setSelectedJob(job);
    else {
      setSelectedJob(undefined);
      setSearchParams((params) => {
        params.delete("job");
        return params;
      });
    }
  }, [jobsList, searchParams, setSearchParams]);

  const displayedJobs = filterStatus
    ? jobsList.filter((j: any) => (j.actionCounts?.[filterStatus] ?? 0) > 0)
    : jobsList;

  return (
    <Row>
      <Col span={24}>
        <Flex vertical style={{
          ...ProjectsMainWrapper,
          fontSize: '16px'
        }}>
          {selectedJob && <BackBtn />}
          <PageHeading job={selectedJob} />
          {!selectedJob && (
            <RecruiterActionDashboard
              onFilterStatus={(status) => setFilterStatus(prev => prev === status ? null : status)}
            />
          )}
          {!selectedJob && filterStatus && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, color: '#555' }}>Filtered by:</span>
              <Tag color="blue" style={{ fontSize: 14 }}>{ACTION_STATUS_LABELS[filterStatus] || filterStatus}</Tag>
              <Button size="small" onClick={() => setFilterStatus(null)}>Clear filter</Button>
            </div>
          )}
          {selectedJob ? (
            <ApplicantsListPanel job={selectedJob} scheduleCall={scheduleCall} />
          ) : (
            <ProjectList list={displayedJobs} />
          )}
        </Flex>
      </Col>
    </Row>
  );
};

export default RecruitersPage;
